import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import mainImg from "../../assets/l1.jpeg";
//import { ChevronsRight } from "lucide-react";
import { Briefcase, FileCheck, Users, Award } from "lucide-react";

/* ================= DATA ================= */
const stats = [
  { value: 500, suffix: "+", label: "Counsellings", icon: Briefcase },
  { value: 200, suffix: "+", label: "Visas", icon: FileCheck },
  { value: 1000, suffix: "+", label: "Clients", icon: Users },
  { value: 100, suffix: "%", label: "Student Satisfied", icon: Award },
];

/* ================= COUNT HOOK ================= */
function useCountUp(end, start) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!start) return;

    let current = 0;
    const duration = 2800;
    const frameRate = 30;
    const totalFrames = duration / frameRate;
    const increment = end / totalFrames;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        current = end;
        clearInterval(timer);
      }
      setCount(Math.floor(current));
    }, frameRate);

    return () => clearInterval(timer);
  }, [end, start]);

  return count;
}
function StatItem({ item, isInView }) {
  const count = useCountUp(item.value, isInView);
  const Icon = item.icon;

  return (
    <motion.div
      variants={featureReveal}
      className="
        relative flex flex-col items-center justify-center
        py-8 px-4 text-center
        bg-gradient-to-br from-blue-500/90 via-blue-600/90 to-blue-800/90
        backdrop-blur-xl
        border border-white/25
        rounded-2xl
        transition-all duration-300
        hover:scale-[1.04]
        hover:shadow-[0_12px_40px_rgba(59,130,246,0.45)]
      "
    >
      {/* Shine */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/15 to-transparent pointer-events-none" />

      {/* ICON */}
      <div className="relative w-12 h-12 rounded-full bg-white/25 flex items-center justify-center mb-3 border border-white/30">
        <Icon size={22} className="text-white" />
      </div>

      {/* NUMBER */}
      <h3 className="relative text-white text-2xl md:text-3xl font-semibold">
        {count}{item.suffix}
      </h3>

      {/* LABEL */}
      <p className="relative mt-1 text-sm text-white/90 tracking-wide">
        {item.label}
      </p>
    </motion.div>
  );
}


/* ================= ANIMATION VARIANTS ================= */
/*const floatBadge = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};*/
const container = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.25,
    },
  },
};

const imageReveal = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};



const scaleBadge = {
  hidden: { opacity: 0, scale: 0.7 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const textReveal = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const featureReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

/*const buttonReveal = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};*/

/* ================= COMPONENT ================= */

export default function Co() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="w-full bg-white py-20 md:py-24">
      <motion.div
        variants={container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="max-w-[1300px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center"
      >

        {/* ================= LEFT IMAGE ================= */}
        <div className="relative mx-auto lg:mx-0
                        w-[300px] h-[300px]
                        sm:w-[360px] sm:h-[360px]
                        md:w-[420px] md:h-[420px]
                        lg:w-[520px] lg:h-[520px]">

          {/* Image */}
          <motion.div
            variants={imageReveal}
            className="w-full h-full overflow-hidden
                       [border-radius:60%_40%_45%_55%/50%_55%_45%_50%]"
          >
            <img src={mainImg} alt="Team" className="w-full h-full object-cover" />
          </motion.div>

          {/* Who We Are 
          <motion.div
            variants={floatBadge}
            className="absolute top-6 sm:top-10 md:top-14 lg:top-16
                       -left-6 sm:-left-8 lg:-left-10
                       bg-white shadow-md rounded-xl
                       px-4 sm:px-5 py-2 sm:py-3
                       flex items-center gap-3"
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-900 text-white flex items-center justify-center text-xs">
              ✔
            </div>
            <span className="text-xs sm:text-sm font-semibold text-blue-800">
              Who We Are
            </span>
          </motion.div>*/}

          {/* Experience */}
          <motion.div
            variants={scaleBadge}
            className="absolute bottom-0 sm:bottom-2
                       right-4 sm:right-8 lg:right-10
                       w-[140px] h-[140px]
                       sm:w-[160px] sm:h-[160px]
                       lg:w-[180px] lg:h-[180px]
                       rounded-full bg-white shadow-lg
                       flex flex-col items-center justify-center"
          >
            <span className="text-3xl lg:text-5xl font-bold text-gray-900">
              8+
            </span>
            <span className="text-xs sm:text-sm text-gray-600 text-center">
              Years Experience <br /> in Field
            </span>

            <div className="absolute bottom-0 left-0 w-full h-full rounded-full
                            border-[12px] lg:border-[14px]
                            border-blue-800
                            border-t-transparent
                            border-r-transparent
                            border-b-transparent" />
          </motion.div>
        </div>

       {/* ================= RIGHT CONTENT ================= */}
<div className="text-center lg:text-left">

  {/* Heading */}
  <motion.h2
    variants={textReveal}
    className="text-3xl sm:text-4xl lg:text-[44px] font-bold mb-10 text-gray-900"
  >
    OUR TRACK RECORD
  </motion.h2>

  {/* Stats Grid */}
  <motion.div
    variants={container}
    className="grid grid-cols-2 gap-6 sm:gap-8"
  >
    {stats.map((item, index) => (
      <StatItem key={index} item={item} isInView={isInView} />
    ))}
  </motion.div>

</div>

      </motion.div>
    </section>
  );
}
